import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Toggler } from 'platform/utilities/feature-toggles';
import SchemaForm from 'platform/forms-system/src/js/components/SchemaForm';
import IntlMobileConfirmModal from '@@vap-svc/components/ContactInformationFieldInfo/IntlMobileConfirmModal';

import {
  clearTransactionRequest,
  createTransaction,
  openModal,
  openIntlMobileConfirmModal,
  refreshTransaction,
  updateFormFieldWithSchema,
  validateAddress,
} from '../actions';
import { createPersonalInfoUpdate } from '../actions/personalInformation';
import { createSchedulingPreferencesUpdate } from '../actions/schedulingPreferences';
import { updateMessagingSignature } from '../../actions/mhv';

import { ACTIVE_EDIT_VIEWS, API_ROUTES, FIELD_NAMES } from '../constants';

import { useEditViewData } from '../hooks/useEditViewData';
import { useEditViewEffects } from '../hooks/useEditViewEffects';
import { useTransactionPolling } from '../hooks/useTransactionPolling';

import { recordCustomProfileEvent } from '../util/analytics';
import { transformInitialFormValues } from '../util/contact-information/formValues';
import {
  getErrorsFromDom,
  handleUpdateButtonClick,
} from '../util/contact-information/addressUtils';
import { getEditViewFormData } from '../util/profile-information-field/getEditViewFormData';
import { getEditViewInputValue } from '../util/profile-information-field/getEditViewInputValue';
import {
  SUBMIT_STRATEGIES,
  getSubmitStrategy,
} from '../util/profile-information-field/getSubmitStrategy';
import { isPendingTransaction } from '../util/transactions';

import CopyMailingAddress from '../containers/CopyMailingAddress';
import { CopyMailingAddress as CopyMailingAddress2 } from '../containers/CopyMailingAddress2';
import { useContactInfoFormAppConfig } from './ContactInfoFormAppConfigContext';
import ProfileInformationActionButtons from './ProfileInformationActionButtons';

/**
 * Functional equivalent of the ProfileInformationEditView class component. The
 * class is unchanged; ProfileInformationFieldController renders this instead
 * when the cveProfileServiceRefactor toggle is on.
 *
 * State arrives through useEditViewData instead of connect(), the lifecycle
 * methods run through useEditViewEffects and useTransactionPolling, and
 * onSubmit executes the strategy described in
 * util/profile-information-field/getSubmitStrategy.
 *
 * Intentional deviations from the class are marked with "PORT NOTE" comments
 * here and in the hooks and helpers this composes.
 *
 * PORT NOTE: the class declared data, editViewData, activeEditView,
 * successCallback and recordCustomProfileEvent in propTypes but never read
 * them, so they are not accepted here. The controller passes some of them
 * anyway, which is harmless.
 */
export const ProfileInformationEditView2 = ({
  allowInternationalPhones,
  apiRoute,
  cancelButtonText,
  convertCleanDataToPayload,
  fieldName,
  forceEditView,
  formSchema,
  getInitialFormValues,
  saveButtonText,
  shouldFocusCancelButton,
  title,
  uiSchema,
  onCancel,
  onCancelButtonFocused,
}) => {
  const dispatch = useDispatch();
  const editFormRef = useRef(null);

  const contactInfoFormAppConfig = useContactInfoFormAppConfig();

  const {
    analyticsSectionName,
    field,
    intlMobileConfirmModalEnabled,
    transaction,
    transactionRequest,
    useProfileServiceRefactor,
  } = useEditViewData(fieldName);

  const onChangeFormDataAndSchemas = (value, schema, newUiSchema) => {
    dispatch(updateFormFieldWithSchema(fieldName, value, schema, newUiSchema));
  };

  useTransactionPolling({
    transaction,
    transactionRequest,
    fieldName,
    onRefresh: () =>
      dispatch(refreshTransaction(transaction, analyticsSectionName)),
  });

  useEditViewEffects({
    field,
    fieldName,
    forceEditView,
    formSchema,
    shouldFocusCancelButton,
    transaction,
    transactionRequest,
    uiSchema,
    editFormRef,
    getInitialFormValues,
    onCancelButtonFocused,
    onClearTransactionRequest: () =>
      dispatch(clearTransactionRequest(fieldName)),
    onOpenAddressValidation: () =>
      dispatch(openModal(ACTIVE_EDIT_VIEWS.ADDRESS_VALIDATION)),
    onUpdateFormData: onChangeFormDataAndSchemas,
  });

  // 48147 - Temporary click handler that will be removed once the analytics
  // stats have been gathered around multiple inline validation errors.
  const onClickUpdateHandler = () => {
    handleUpdateButtonClick(
      getErrorsFromDom,
      fieldName,
      recordCustomProfileEvent,
    );
  };

  const copyMailingAddress = mailingAddress => {
    const newAddressValue = { ...field.value, ...mailingAddress };
    onChangeFormDataAndSchemas(
      transformInitialFormValues(newAddressValue),
      field.formSchema,
      field.uiSchema,
    );
  };

  const onInput = (value, schema, newUiSchema) => {
    onChangeFormDataAndSchemas(
      getEditViewInputValue({ fieldName, value }),
      schema,
      newUiSchema,
    );
  };

  const onSubmit = () => {
    const strategy = getSubmitStrategy({
      fieldName,
      field,
      apiRoute,
      analyticsSectionName,
      convertCleanDataToPayload,
    });

    const createTransactionFn = () =>
      dispatch(
        createTransaction(
          apiRoute,
          strategy.method,
          fieldName,
          strategy.payload,
          analyticsSectionName,
        ),
      );

    switch (strategy.type) {
      case SUBMIT_STRATEGIES.REQUIRE_FIELD:
        onChangeFormDataAndSchemas(
          strategy.value,
          strategy.formSchema,
          strategy.uiSchema,
        );
        break;

      case SUBMIT_STRATEGIES.MESSAGING_SIGNATURE:
        dispatch(
          updateMessagingSignature(
            strategy.payload,
            fieldName,
            strategy.method,
          ),
        );
        break;

      case SUBMIT_STRATEGIES.PERSONAL_INFO:
        dispatch(createPersonalInfoUpdate(strategy.request));
        break;

      case SUBMIT_STRATEGIES.SCHEDULING_PREFERENCES:
        dispatch(createSchedulingPreferencesUpdate(strategy.request));
        break;

      case SUBMIT_STRATEGIES.VALIDATE_ADDRESS:
        dispatch(
          validateAddress(
            apiRoute,
            strategy.method,
            fieldName,
            strategy.payload,
            analyticsSectionName,
          ),
        );
        break;

      case SUBMIT_STRATEGIES.CONFIRM_INTL_MOBILE:
        // Relay the transaction to the confirmation modal, which runs it once
        // the Veteran confirms the international number.
        dispatch(
          openIntlMobileConfirmModal(
            strategy.countryCode,
            strategy.phoneNumber,
            createTransactionFn,
          ),
        );
        break;

      default:
        createTransactionFn();
        break;
    }
  };

  const isLoading =
    transactionRequest?.isPending || isPendingTransaction(transaction);

  const isResidentialAddress = fieldName === FIELD_NAMES.RESIDENTIAL_ADDRESS;

  const CopyMailingAddressComponent = useProfileServiceRefactor
    ? CopyMailingAddress2
    : CopyMailingAddress;

  const formData = getEditViewFormData({
    field,
    fieldName,
    formFieldData: contactInfoFormAppConfig?.formFieldData,
  });

  return (
    <>
      {!!field && (
        <div ref={editFormRef}>
          {isResidentialAddress && (
            <CopyMailingAddressComponent
              copyMailingAddress={copyMailingAddress}
            />
          )}

          <SchemaForm
            addNameAttribute
            // `name` and `title` are required by SchemaForm, but are only used
            // internally by the SchemaForm component
            name="Contact Info Form"
            title="Contact Info Form"
            schema={field.formSchema}
            data={formData}
            uiSchema={field.uiSchema}
            formOptions={contactInfoFormAppConfig?.formOptions}
            onChange={event => onInput(event, field.formSchema, field.uiSchema)}
            onSubmit={onSubmit}
          >
            {fieldName === FIELD_NAMES.MOBILE_PHONE &&
              allowInternationalPhones && (
                <Toggler.Hoc
                  toggleName={
                    Toggler.TOGGLE_NAMES.profileInternationalPhoneNumbers
                  }
                >
                  {toggleValue =>
                    toggleValue ? (
                      <p>
                        Enter a U.S. mobile phone number to receive text
                        notifications. We can’t send text notifications to
                        international numbers.
                      </p>
                    ) : null
                  }
                </Toggler.Hoc>
              )}
            <ProfileInformationActionButtons
              onCancel={onCancel}
              title={title}
              analyticsSectionName={analyticsSectionName}
              isLoading={isLoading}
            >
              <div className="vads-u-display--block mobile-lg:vads-u-display--flex">
                <va-button
                  data-action="save-edit"
                  data-testid="save-edit-button"
                  loading={isLoading}
                  submit="prevent"
                  onClick={onClickUpdateHandler}
                  text={isLoading ? '' : saveButtonText || 'Save'}
                  class="vads-u-margin-top--1 vads-u-margin-bottom--1 vads-u-width--full mobile-lg:vads-u-width--auto"
                />

                {!isLoading && (
                  <va-button
                    data-testid="cancel-edit-button"
                    onClick={onCancel}
                    text={cancelButtonText || 'Cancel'}
                    class="vads-u-margin-top--1 vads-u-width--full mobile-lg:vads-u-width--auto"
                    secondary
                  />
                )}
              </div>
            </ProfileInformationActionButtons>
          </SchemaForm>
        </div>
      )}
      {intlMobileConfirmModalEnabled && <IntlMobileConfirmModal />}
    </>
  );
};

ProfileInformationEditView2.propTypes = {
  apiRoute: PropTypes.oneOf(Object.values(API_ROUTES)).isRequired,
  convertCleanDataToPayload: PropTypes.func.isRequired,
  fieldName: PropTypes.oneOf(Object.values(FIELD_NAMES)).isRequired,
  formSchema: PropTypes.object.isRequired,
  getInitialFormValues: PropTypes.func.isRequired,
  uiSchema: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  allowInternationalPhones: PropTypes.bool,
  cancelButtonText: PropTypes.string,
  forceEditView: PropTypes.bool,
  saveButtonText: PropTypes.string,
  shouldFocusCancelButton: PropTypes.bool,
  title: PropTypes.string,
  onCancelButtonFocused: PropTypes.func,
};

export default ProfileInformationEditView2;
